/**
 * Shared Guide / channel-page timeline geometry.
 * One time system: nowLeft = (now - windowStart) / span.
 * Pages keep their own px-per-hour and program-segment mapping.
 */

function computeGuideTimeline(from, to, nowMs) {
  nowMs = nowMs != null ? nowMs : Date.now();
  var start = from ? new Date(from).getTime() : nowMs;
  var end = to ? new Date(to).getTime() : start + 6 * 3600000;
  if (!isFinite(start)) {
    start = nowMs;
  }
  if (!isFinite(end) || end <= start) {
    end = start + 6 * 3600000;
  }
  var span = Math.max(end - start, 60 * 60 * 1000);
  var hourMs = 60 * 60 * 1000;
  var firstHour = Math.floor(start / hourMs) * hourMs;
  var hours = [];
  for (var t = firstHour; t < end; t += hourMs) {
    hours.push(t);
  }
  var nowLeft = ((nowMs - start) / span) * 100;
  return {
    start: start,
    end: end,
    span: span,
    hours: hours,
    nowLeft: nowLeft,
    hourMs: hourMs,
  };
}

/**
 * True when Scheduler intentionally left ends_at open (24/7 / continuous airing).
 * Null/empty ends_at is open-ended; finite ends_at is never treated as 24/7.
 */
function isOpenEndedProgram(p) {
  if (!p) return false;
  // Explicit finite end always wins — never treat a dated program as 24/7.
  if (p.ends_at != null && p.ends_at !== "") {
    return false;
  }
  if (p.is_open_ended === true || p.is_open_ended === 1 || p.is_open_ended === "1") {
    return true;
  }
  if (p.is_open_ended === false || p.is_open_ended === 0 || p.is_open_ended === "0") {
    return false;
  }
  // Scheduler stores intentional 24/7 as null ends_at with a real starts_at.
  return !!p.starts_at;
}

/**
 * Finite duration only. Open-ended programs must not invent a permanent length;
 * callers clip presentation to the Guide window via resolveProgramEndMs.
 */
function durationMs(p) {
  if (!p) return 30 * 60 * 1000;
  if (isOpenEndedProgram(p)) {
    return null;
  }
  if (p.duration_seconds != null && Number(p.duration_seconds) > 0) {
    return Number(p.duration_seconds) * 1000;
  }
  if (p.starts_at && p.ends_at) {
    var ms = new Date(p.ends_at).getTime() - new Date(p.starts_at).getTime();
    return ms > 0 ? ms : 30 * 60 * 1000;
  }
  return 30 * 60 * 1000;
}

/**
 * Presentation end for a program inside a Guide window.
 * Open-ended → clip to next program start (if any) else window end.
 * Never invents a DB ends_at.
 *
 * @param {object} p
 * @param {number} windowEndMs
 * @param {number|null|undefined} nextStartsAtMs
 * @returns {number}
 */
function resolveProgramEndMs(p, windowEndMs, nextStartsAtMs) {
  var windowEnd = isFinite(windowEndMs) ? windowEndMs : Date.now() + 6 * 3600000;
  if (!isOpenEndedProgram(p) && p && p.ends_at) {
    var explicit = new Date(p.ends_at).getTime();
    if (isFinite(explicit)) {
      return explicit;
    }
  }
  var clip = windowEnd;
  if (
    nextStartsAtMs != null &&
    isFinite(nextStartsAtMs) &&
    nextStartsAtMs > 0
  ) {
    clip = Math.min(clip, nextStartsAtMs);
  }
  return clip;
}

/**
 * Position a program card inside the visible Guide window.
 * Returns null when the program does not intersect the window.
 *
 * @param {object} p
 * @param {number} windowStartMs
 * @param {number} windowEndMs
 * @param {number|null|undefined} nextStartsAtMs
 * @returns {{left:number,width:number,pStart:number,pEnd:number,isOpenEnded:boolean}|null}
 */
function programSegmentGeometry(p, windowStartMs, windowEndMs, nextStartsAtMs) {
  var windowStart = isFinite(windowStartMs) ? windowStartMs : Date.now();
  var windowEnd =
    isFinite(windowEndMs) && windowEndMs > windowStart
      ? windowEndMs
      : windowStart + 6 * 3600000;
  var span = Math.max(windowEnd - windowStart, 60 * 60 * 1000);

  var pStart = p && p.starts_at ? new Date(p.starts_at).getTime() : windowStart;
  if (!isFinite(pStart)) {
    pStart = windowStart;
  }

  var openEnded = isOpenEndedProgram(p);
  var pEnd;
  if (openEnded) {
    pEnd = resolveProgramEndMs(p, windowEnd, nextStartsAtMs);
  } else if (p && p.ends_at) {
    pEnd = new Date(p.ends_at).getTime();
    if (!isFinite(pEnd)) {
      var finiteDur = durationMs(p);
      pEnd = pStart + (finiteDur != null ? finiteDur : 30 * 60 * 1000);
    }
  } else {
    // Malformed finite row without ends_at and without open-ended flag —
    // keep a short stub so it remains discoverable, not a full-window fill.
    pEnd = pStart + 30 * 60 * 1000;
  }

  var visibleStart = Math.max(pStart, windowStart);
  var visibleEnd = Math.min(pEnd, windowEnd);
  if (!(visibleEnd > visibleStart)) {
    return null;
  }

  var left = ((visibleStart - windowStart) / span) * 100;
  var width = ((visibleEnd - visibleStart) / span) * 100;

  return {
    left: Math.max(0, left),
    width: Math.max(0.75, width),
    pStart: pStart,
    pEnd: pEnd,
    isOpenEnded: openEnded,
  };
}

/**
 * Match EPG row ↔ resolveNow current payload (id formats differ).
 */
function isSameGuideProgram(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && String(a.id) === String(b.id)) {
    return true;
  }
  if (
    a.unique_id &&
    b.unique_id &&
    String(a.unique_id) === String(b.unique_id)
  ) {
    return true;
  }
  if (
    a.unique_id &&
    b.id != null &&
    String(a.unique_id) === String(b.id)
  ) {
    return true;
  }
  if (
    b.unique_id &&
    a.id != null &&
    String(b.unique_id) === String(a.id)
  ) {
    return true;
  }
  if (
    a.schedule_entry_id != null &&
    b.schedule_entry_id != null &&
    String(a.schedule_entry_id) === String(b.schedule_entry_id)
  ) {
    return true;
  }
  if (
    a.schedule_entry_id != null &&
    b.id != null &&
    String(a.schedule_entry_id) === String(b.id)
  ) {
    return true;
  }
  if (
    b.schedule_entry_id != null &&
    a.id != null &&
    String(b.schedule_entry_id) === String(a.id)
  ) {
    return true;
  }
  if (a.starts_at && b.starts_at && String(a.starts_at) === String(b.starts_at)) {
    return true;
  }
  return false;
}

module.exports = {
  computeGuideTimeline: computeGuideTimeline,
  isOpenEndedProgram: isOpenEndedProgram,
  durationMs: durationMs,
  resolveProgramEndMs: resolveProgramEndMs,
  programSegmentGeometry: programSegmentGeometry,
  isSameGuideProgram: isSameGuideProgram,
};
